import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  id?: string;
  name?: string;
}

const InputContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.spacing.md};
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
`;

const InputLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.deepDark};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
    margin-bottom: ${theme.spacing.sm};
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{ hasError?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 1rem;
  width: 100%;
  transition: all 0.3s ease;
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  min-height: ${theme.mobile.minTouchTarget};
  /* Prevent zoom on iOS */
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${props => (props.hasError ? theme.colors.error : theme.colors.primary)};
    box-shadow: 0 4px 12px ${props => 
      props.hasError 
        ? 'rgba(228, 139, 136, 0.2)' 
        : 'rgba(247, 173, 121, 0.2)'
    };
    transform: translateY(-2px);
  }
  
  &::placeholder {
    color: #aaa;
    font-weight: 300;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    border-radius: 12px;
    font-size: 16px; /* Prevent zoom on iOS */
    
    &:focus {
      transform: none;
      box-shadow: 0 2px 8px ${props => 
        props.hasError 
          ? 'rgba(228, 139, 136, 0.15)' 
          : 'rgba(247, 173, 121, 0.15)'
      };
    }
  }
`;

const IconContainer = styled.div`
  position: absolute;
  right: ${theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.darkAccent};
  min-height: ${theme.mobile.minTouchTarget};
  min-width: ${theme.mobile.minTouchTarget};
`;

const ErrorMessage = styled.span`
  color: ${theme.colors.error};
  font-size: 0.75rem;
  margin-top: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.875rem;
    margin-top: ${theme.spacing.sm};
  }
`;

const Input: React.FC<InputProps> = ({
  label,
  error,
  fullWidth = false,
  icon,
  id,
  name,
  ...rest
}) => {
  // Generate a unique ID if not provided
  const inputId = id || `input-${label?.toLowerCase().replace(/\s+/g, '-')}-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <InputContainer fullWidth={fullWidth}>
      {label && <InputLabel htmlFor={inputId}>{label}</InputLabel>}
      <InputWrapper>
        <StyledInput 
          id={inputId} 
          name={name || inputId} 
          hasError={!!error} 
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...rest} 
        />
        {icon && <IconContainer>{icon}</IconContainer>}
      </InputWrapper>
      {error && <ErrorMessage id={`${inputId}-error`}>{error}</ErrorMessage>}
    </InputContainer>
  );
};

export default Input; 